import { ImageOff, X } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Popover, PopoverAnchor, PopoverContent } from '@components/ui/popover'
import { Skeleton } from '@components/ui/skeleton'
import { usePhotoTrayHoverPreview } from '@/hooks/use-photo-tray-hover-preview'
import {
  PHOTO_TRAY_PREVIEW_ALIGN_OFFSET,
  PHOTO_TRAY_PREVIEW_ESCAPE_DISMISS_EVENT,
  PHOTO_TRAY_PREVIEW_IMAGE_HEIGHT,
  PHOTO_TRAY_PREVIEW_IMAGE_WIDTH,
} from '@/lib/photos/photo-tray-hover-preview'
import { preloadTrayPreviewImage } from '@/lib/photos/preload-tray-preview-image'
import { cn } from '@/utils'
import { TrayPlacedBadge, type TrayPlacedBadgePlacement } from '../components/tray-placed-badge'

export type TrayThumbnailCardProps = {
  /** Stable identity for the thumbnail — resets the broken-image fallback when it changes. */
  itemKey: string
  /** Human-readable name: the caption text and the base of the preview's accessible name. */
  label: string
  /** Accessible name of the card itself (describes what Enter/Space does). */
  ariaLabel: string
  /** Grid thumbnail source. Missing or failing → broken-image placeholder. */
  imageUrl?: string
  /** Enlarged hover-preview source. Missing or failing → broken-image placeholder. */
  previewUrl?: string
  /** Show `label` under the image. */
  showCaption?: boolean
  /** Show the "Placed" badge in the image's bottom-left corner. */
  placed?: boolean
  /** Where the badge sits: `'inside'` the image (default) or `'overlap-bottom'`, centred on the
   *  image's bottom edge as ClikArt's story specifies (WMPCM-13312). */
  placedBadgePlacement?: TrayPlacedBadgePlacement
  /** Outline the card in the primary colour — the Templates tray marks the template most recently
   *  placed on the spread this way (US-13). */
  highlighted?: boolean
  /** 1 = large single-column card; 2 = compact two-column grid (default). */
  columns?: 1 | 2
  /** Overrides the media box's size/aspect — defaults to the 1-column/2-column Photos sizing
   *  (`--panel-single-photo-height` / `aspect-[130/97.5]`). Set when a caller's items have a
   *  fundamentally different shape, e.g. Templates' wide spread previews. */
  mediaAspectClassName?: string
  /** How the image sits in the thumbnail and the enlarged preview. `'cover'` (default) fills the
   *  box and crops the overflow, right for photos and art. `'contain'` shows the whole image,
   *  letterboxed on the muted background — for items that must not lose their edges, e.g.
   *  Templates' wide two-page spreads. */
  imageFit?: 'cover' | 'contain'
  /** Render the card faded, e.g. while it is the item being dragged. */
  opacity?: number
  /** Whether hovering/focusing may open the enlarged preview (e.g. false mid-drag). */
  previewEnabled?: boolean
  /** Accessible role for the card shell — `'button'` (default) for an item with a real action
   *  (Photos' drag-to-place); pass `'img'` for a purely browsable item with no action yet (e.g.
   *  Templates, before apply-template lands), matching `BackgroundThumbnail`'s own reasoning. */
  role?: 'button' | 'img'
  /** Primary-button pointer down on the card. The preview is already closed when this runs. */
  onPointerDown?: (event: React.PointerEvent<HTMLDivElement>, card: HTMLDivElement) => void
  /** Enter/Space on the focused card, with its on-screen rect. The preview is already closed. */
  onActivate?: (rect: DOMRect) => void
  /** Accessible name of the corner remove button; the button renders only when this is set. */
  removeLabel?: string
  onRemove?: () => void
  removeDisabled?: boolean
  /** Reports hover/focus preview open state for host Escape priority chains (WMPCM-11088). */
  onPreviewOpenChange?: (open: boolean) => void
}

function TrayBrokenImagePlaceholder() {
  return (
    <div
      className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground"
      role="img"
      aria-label="Broken image"
    >
      <ImageOff className="size-6" aria-hidden="true" />
    </div>
  )
}

/**
 * Presentational right-panel tray thumbnail: image (with broken-image fallback), optional
 * "Placed" badge / caption / remove button, and a delayed hover/focus enlarged preview.
 *
 * Knows nothing about what the item is or what picking it up does — domain wrappers (e.g.
 * `PhotoTrayThumbnail`) supply `onPointerDown` / `onActivate`.
 */
export function TrayThumbnailCard({
  itemKey,
  label,
  ariaLabel,
  imageUrl,
  previewUrl,
  showCaption = false,
  placed = false,
  placedBadgePlacement = 'inside',
  highlighted = false,
  columns = 2,
  mediaAspectClassName,
  imageFit = 'cover',
  opacity = 1,
  previewEnabled = true,
  role = 'button',
  onPointerDown,
  onActivate,
  removeLabel,
  onRemove,
  removeDisabled = false,
  onPreviewOpenChange,
}: TrayThumbnailCardProps) {
  const shellRef = useRef<HTMLDivElement>(null)
  const isSingleColumn = columns === 1
  const imageRef = useRef<HTMLImageElement>(null)
  const [imageFailed, setImageFailed] = useState(false)
  const [previewFailed, setPreviewFailed] = useState(false)
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null)

  useEffect(() => {
    setImageFailed(false)

    // One the browser already has (cached) can be complete before `onLoad` is attached, so ask the
    // element instead of waiting for an event that never comes.
    const image = imageRef.current

    if (imageUrl && image?.complete && image.naturalWidth > 0) {
      setLoadedUrl(imageUrl)
    }
  }, [itemKey, imageUrl])

  const isImageLoaded = Boolean(imageUrl) && loadedUrl === imageUrl

  useEffect(() => {
    setPreviewFailed(false)
  }, [itemKey, previewUrl])

  const showImage = Boolean(imageUrl) && !imageFailed
  const overlapsBottom = placedBadgePlacement === 'overlap-bottom'
  const imageClassName = cn(
    'absolute inset-0 h-full w-full pointer-events-none select-none',
    imageFit === 'contain' ? 'object-contain' : 'object-cover'
  )

  const {
    open: previewOpen,
    onTriggerPointerEnter,
    onTriggerPointerLeave,
    onTriggerFocus,
    onTriggerBlur,
    onContentPointerEnter,
    onContentPointerLeave,
    closeNow: closePreview,
  } = usePhotoTrayHoverPreview(previewEnabled)

  // Report only while open, and clear in the cleanup. React runs every cleanup before any new
  // effect in a render, so when a new card's preview opens as the old card's closes, the old
  // card's `false` always lands before the new card's `true`, whichever comes first in the grid.
  // A bare `onPreviewOpenChange(previewOpen)` let the last report win and could leave "no preview
  // open" while one was showing, so Escape closed the whole panel.
  useEffect(() => {
    if (!previewOpen) return

    onPreviewOpenChange?.(true)
    return () => onPreviewOpenChange?.(false)
  }, [onPreviewOpenChange, previewOpen])

  useEffect(() => {
    const onDismiss = () => closePreview()

    window.addEventListener(PHOTO_TRAY_PREVIEW_ESCAPE_DISMISS_EVENT, onDismiss)
    return () => window.removeEventListener(PHOTO_TRAY_PREVIEW_ESCAPE_DISMISS_EVENT, onDismiss)
  }, [closePreview])

  // True from a primary pointer press until the pointer is released. A press focuses the card
  // (the card is focusable and a mouse-down's default is to focus), and that focus must not read as
  // keyboard focus: it would re-open the preview 300 ms after the press, and, since the card keeps
  // focus, keep it open through the drag and after release. Only real keyboard focus opens it.
  const pointerPressedRef = useRef(false)

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button > 0) return

      pointerPressedRef.current = true

      const endPress = () => {
        pointerPressedRef.current = false
        window.removeEventListener('pointerup', endPress)
        window.removeEventListener('pointercancel', endPress)
      }

      window.addEventListener('pointerup', endPress)
      window.addEventListener('pointercancel', endPress)
      closePreview()
      onPointerDown?.(e, shellRef.current ?? e.currentTarget)
    },
    [closePreview, onPointerDown]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape' && previewOpen) {
        e.preventDefault()
        e.stopPropagation()
        closePreview()
        return
      }

      if (!onActivate) return
      if (e.key !== 'Enter' && e.key !== ' ') return
      e.preventDefault()
      closePreview()
      const rect = shellRef.current?.getBoundingClientRect()

      if (!rect || rect.width <= 0) return
      onActivate(rect)
    },
    [closePreview, onActivate, previewOpen]
  )

  return (
    <div
      className={cn(
        'min-w-0',
        isSingleColumn ? 'w-full max-w-[var(--panel-single-photo-width)]' : 'w-full'
      )}
      style={{ opacity }}
    >
      <Popover open={previewOpen} modal={false}>
        <PopoverAnchor asChild>
          <div
            ref={shellRef}
            role={role}
            tabIndex={0}
            aria-expanded={previewOpen}
            aria-label={ariaLabel}
            className={cn(
              'relative flex w-full min-w-0 flex-col gap-1 rounded-md border bg-background p-1',
              highlighted ? 'border-primary ring-1 ring-primary' : 'border-border',
              'cursor-grab active:cursor-grabbing touch-none select-none',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            )}
            onPointerEnter={(event) => {
              // A button held while crossing onto this card is a drag in progress (from another
              // card, or from outside the tray): it never previews.
              if (event.buttons > 0) return

              // Warm the cache during the open delay so the preview paints as soon as it opens.
              if (previewEnabled && event.pointerType !== 'touch') {
                preloadTrayPreviewImage(previewUrl)
              }

              onTriggerPointerEnter(event.pointerType)
            }}
            onPointerLeave={onTriggerPointerLeave}
            onFocus={() => {
              if (pointerPressedRef.current) return

              if (previewEnabled) preloadTrayPreviewImage(previewUrl)
              onTriggerFocus()
            }}
            onBlur={onTriggerBlur}
            onPointerDown={handlePointerDown}
            onKeyDown={handleKeyDown}
          >
            {/* Not clipped, so an overlapping badge can hang below the image; the extra bottom
             *  margin keeps it clear of the caption. */}
            <div className={cn('relative w-full', overlapsBottom && 'mb-1.5')}>
              <div
                className={cn(
                  'relative isolate w-full overflow-hidden rounded-md',
                  // The muted fill is the letterbox / placeholder behind a loaded or missing image.
                  // While the image downloads the skeleton is the fill, so the pulse has a
                  // contrasting card background to pulse against.
                  (isImageLoaded || !showImage) && 'bg-muted',
                  mediaAspectClassName ??
                    (isSingleColumn ? 'h-[var(--panel-single-photo-height)]' : 'aspect-[130/97.5]')
                )}
              >
                {showImage ? (
                  <>
                    {!isImageLoaded ? (
                      <Skeleton
                        className="absolute inset-0 h-full w-full rounded-md motion-reduce:animate-none"
                        data-testid="tray-thumbnail-skeleton"
                      />
                    ) : null}
                    <img
                      ref={imageRef}
                      src={imageUrl}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className={cn(imageClassName, !isImageLoaded && 'opacity-0')}
                      draggable={false}
                      onLoad={() => setLoadedUrl(imageUrl ?? null)}
                      onError={() => setImageFailed(true)}
                    />
                  </>
                ) : (
                  <TrayBrokenImagePlaceholder />
                )}
                {placed && !overlapsBottom ? <TrayPlacedBadge /> : null}
              </div>
              {placed && overlapsBottom ? <TrayPlacedBadge placement="overlap-bottom" /> : null}
            </div>
            {showCaption ? (
              <p
                className="w-full truncate px-1 text-left text-xs font-normal leading-4 text-foreground"
                title={label}
              >
                {label}
              </p>
            ) : null}
            {removeLabel ? (
              <button
                type="button"
                aria-label={removeLabel}
                className="absolute top-1 right-1 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm hover:bg-background"
                onClick={(e) => {
                  e.stopPropagation()
                  onRemove?.()
                }}
                onPointerDown={(e) => e.stopPropagation()}
                disabled={removeDisabled}
              >
                <X className="h-3 w-3" />
              </button>
            ) : null}
          </div>
        </PopoverAnchor>
        <PopoverContent
          side="left"
          align="center"
          alignOffset={PHOTO_TRAY_PREVIEW_ALIGN_OFFSET}
          sideOffset={0}
          avoidCollisions
          collisionPadding={8}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => {
            event.preventDefault()
            closePreview()
          }}
          onPointerEnter={onContentPointerEnter}
          onPointerLeave={onContentPointerLeave}
          onPointerDown={(event) => event.stopPropagation()}
          className="z-[60] w-auto p-4"
          aria-label={`Preview of ${label}`}
        >
          <div
            className="relative overflow-hidden rounded-md"
            style={{
              width: PHOTO_TRAY_PREVIEW_IMAGE_WIDTH,
              height: PHOTO_TRAY_PREVIEW_IMAGE_HEIGHT,
            }}
          >
            {previewUrl && !previewFailed ? (
              <img
                src={previewUrl}
                alt=""
                className={imageClassName}
                draggable={false}
                onError={() => setPreviewFailed(true)}
              />
            ) : (
              <TrayBrokenImagePlaceholder />
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
